import { entityPaths } from '@lakaran/geometry';
import { pickablePieces, type NodeGraph } from '@lakaran/nodes';
import type { CadEditor } from '../src/editor/editor';
import {
  createPieceHighlighter,
  createPiecePicker,
  type PickPiece,
  type PieceHighlighter,
  type PiecePicker,
} from './piece-picker';

/**
 * Pieces excluded from a merge, picked in the drawing.
 *
 * A merge welds everything wired into it; sometimes a construction line or a centre line must stay
 * out of that weld without being deleted. Clicking a piece toggles it into the exclusion list: it
 * passes through the step untouched, exactly as drawn. `X` / `Esc` / right-click ends the mode.
 */

export interface ExcludePickerOptions {
  graph: () => NodeGraph;
  /** Adds or removes one piece from the exclusion list. */
  onToggle: (nodeId: string, key: string, excluded: boolean) => void;
  onEnd?: () => void;
}

export type ExcludePicker = PiecePicker;
export type ExcludeHighlighter = PieceHighlighter;

const piecesOf = (graph: NodeGraph, nodeId: string, layerId: string): PickPiece[] =>
  pickablePieces(graph, nodeId, layerId).map((piece) => ({
    key: piece.key,
    label: piece.label,
    on: piece.picked,
    paths: entityPaths(piece.entity),
  }));

export function createExcludePicker(
  editor: CadEditor,
  host: HTMLElement,
  options: ExcludePickerOptions,
): ExcludePicker {
  return createPiecePicker(editor, host, {
    pieces: (nodeId) => piecesOf(options.graph(), nodeId, editor.doc.currentLayerId),
    onToggle: (nodeId, key, picked) => options.onToggle(nodeId, key, picked),
    hint: 'Merge — click a piece to leave it out of the weld, click again to include it, X or Esc ends',
    startMessage: (count) =>
      `MERGE  ${count} pieces in this step — click the ones to leave out of the weld (X ends).`,
    emptyMessage: 'MERGE  Nothing to exclude — wire shapes into A or B first.',
    action: (piece) => (piece.on ? 'click to include it again' : 'click to leave it out'),
    onClass: 'is-picked',
    onEnd: options.onEnd,
  });
}

/** Hovering an excluded row in the card lights up the piece it would put back. */
export function createExcludeHighlighter(
  editor: CadEditor,
  host: HTMLElement,
  options: { graph: () => NodeGraph },
): ExcludeHighlighter {
  return createPieceHighlighter(editor, host, {
    pieces: (nodeId) => piecesOf(options.graph(), nodeId, editor.doc.currentLayerId),
    hotClass: 'is-picked',
  });
}
